import Feature from 'ol/Feature.js';
import { GeoJSONFeature } from 'ol/format/GeoJSON';
import Point from 'ol/geom/Point.js';
import Draw from 'ol/interaction/Draw';
import VectorLayer from 'ol/layer/Vector';
import Map from 'ol/Map';
import { fromLonLat } from 'ol/proj.js';
import VectorSource from 'ol/source/Vector';
import { Icon } from 'ol/style.js';
import Fill from 'ol/style/Fill';
import Style from 'ol/style/Style';
import Text from 'ol/style/Text';
import { toast } from 'react-toastify';
import missedEventMarkerIcon from '../static/missedeventmarker.png';

export function missedEventCoordinatesFix(coords: number[], started_date: string): number[] {
    const coordinates = coords[1] < coords[0] && new Date(started_date).getFullYear() < 2026 ?
        [coords[1], coords[0]] :
        coords;
    return coordinates;
}
export class MissedEventsService {
    private readonly map: Map;
    private missedEventsLayer: VectorLayer<VectorSource>;
    private reportMissedEventLayer: VectorLayer<VectorSource>;
    private reportMissedEventDraw: Draw | null = null;

    constructor(map: Map) {
        this.map = map;

        this.missedEventsLayer = new VectorLayer({
            source: new VectorSource({
                features: [],
            }),
            visible: false,
            zIndex: 998,
        });
        this.map.addLayer(this.missedEventsLayer);

        this.reportMissedEventLayer = new VectorLayer({
            source: new VectorSource(),
            style: {
                'fill-color': 'rgba(255, 255, 255, 0.2)',
                'stroke-color': '#ffcc33',
                'stroke-width': 2,
                'circle-radius': 7,
                'circle-fill-color': '#ffcc33',
            },
        });
    }

    isDrawing(): boolean {
        return this.reportMissedEventDraw !== null;
    }

    isLayerVisible(): boolean {
        return this.missedEventsLayer.getVisible();
    }

    getMissedEventsLayer(): VectorLayer<VectorSource> {
        return this.missedEventsLayer;
    }

    async show(): Promise<GeoJSONFeature[]> {
        return fetch(`/api/missed-events/missed-events/?format=json`, {
            method: "GET",
            credentials: "include",
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
            }
        }).then((response) => {
            if (response.status === 200) {
                return response.json();
            } else {
                toast.error("Error fetching missed events, please try again");
            }
        }).then((json: {
            type: string,
            features: GeoJSONFeature[],
        }) => {
            const items: GeoJSONFeature[] =
                (json?.features instanceof Array && json.features) ||
                (json instanceof Array ? json : []);

            if (items.length > 0) {
                this.clear();
                const style = new Style({
                    image: new Icon({
                        anchor: [-.08, 20],
                        anchorXUnits: 'fraction',
                        anchorYUnits: 'pixels',
                        src: missedEventMarkerIcon,
                        scale: 0.8,
                    })
                });
                const source = this.missedEventsLayer.getSource();
                for (const item of items) {
                    // older points from the old webapp have flipped lon lat to lat lon
                    // so in that case [1] (lon) will be bigger than [0] (lat) for *most* points
                    // but not all, so add the year check as well as points reported after that
                    // will have the correct order
                    const coordinates = missedEventCoordinatesFix(item.geometry.coordinates as number[], item.properties.started_date);

                    const feature = new Feature({
                        geometry: new Point(fromLonLat(coordinates, "EPSG:3857")),
                        name: "missed-event",
                    });
                    const personalStyle = style.clone();
                    personalStyle.setText(new Text({
                        text: item.properties.started_date,
                        offsetY: 35,
                        font: '14px sans-serif',
                        fill: new Fill({ color: '#00000099' })
                    }));
                    feature.setStyle(personalStyle);
                    feature.setProperties(item?.properties || item);
                    source!.addFeature(feature);
                }
                this.missedEventsLayer.setVisible(true);
            }
            return items;
        }).catch((error) => {
            console.error("Error fetching missed events", error);
            toast.error("Error fetching missed events, please try again");
            return [];
        });
    }

    clear(): void {
        this.missedEventsLayer.getSource()?.clear();
        this.missedEventsLayer.setVisible(false);
    }

    addInteraction(modifyEndCallback: (feature: Feature) => void): void {
        this.clearInteraction();
        this.reportMissedEventLayer.setVisible(true);
        this.map.addLayer(this.reportMissedEventLayer);
        this.reportMissedEventDraw = new Draw({
            source: this.missedEventsLayer.getSource() as VectorSource,
            type: 'Point',
        });

        this.reportMissedEventDraw.on('drawend', (evt) => {
            evt.stopPropagation();
            evt.preventDefault();
            const feature = evt.feature;

            if (feature) {
                modifyEndCallback(feature);
            }
            if (this.reportMissedEventDraw) this.map.removeInteraction(this.reportMissedEventDraw);
        });

        this.map.addInteraction(this.reportMissedEventDraw);
    }

    clearInteraction(): void {
        const src = this.reportMissedEventLayer.getSource();
        if (src) {
            src.clear();
        }
        this.map.removeLayer(this.reportMissedEventLayer);
        if (this.reportMissedEventDraw) {
            this.map.removeInteraction(this.reportMissedEventDraw);
            this.reportMissedEventDraw = null;
        }
    }
}
